// needs-decision-interview-questions.ts — B4 nửa còn lại
// (plan 20260915-1650, phiên 2026-09-16).
//
// Dịch một result `outcome: "needs_decision"` của worker thành câu hỏi
// interview có ID ỔN ĐỊNH THEO NỘI DUNG (V2-0, xem `needsDecisionQuestionId`).
// Đây là caller production ĐẦU TIÊN của
// `writeInterviewQuestions`: trước phiên này P4 đã dựng đủ "lưu + gác"
// (interview-store.ts + gate `interview_unanswered`) nhưng KHÔNG đường nào đẻ
// ra câu hỏi — grep verbatim 2026-09-16: 0 caller ngoài 4 test file. Cơ chế
// đúng mà không ai gọi là code chết, đúng lớp lỗi P20/P21 và đúng lý do D1 bị
// bác ở P5.
//
// Lỗ thứ hai module này đóng: `needs_decision` có 0 consumer trong `src/`
// (2 hit đều ở autopilot-result-schema.ts). `routeAttemptOutcome` không có
// nhánh cho nó ⇒ attempt rơi xuống `recordAttemptOutcome` như một FAIL thường
// ⇒ `counted_failures + 1`. Worker khai đúng contract "tôi cần người quyết"
// bị phạt y như worker làm hỏng việc.
//
// ─────────────────────────────────────────────────────────────────────────
// Vì sao `required: true` — và vì sao đó KHÔNG phải Bug K
// ─────────────────────────────────────────────────────────────────────────
// Bug K = worker tự khai ràng buộc governing chính worker. Ở đây cờ `required`
// do CONTROLLER hardcode ngay trong file này; worker chỉ cấp NỘI DUNG
// (`options[]`, `summary`). Worker không đổi được cờ theo bất kỳ chiều nào —
// `true` hay `false` đều nằm ngoài tầm với của nó. Cùng khuôn với
// `checkAcceptanceDoneHasEvidence` (đọc ticket của controller) và
// `commandAdapter` (adapter do controller gán, không theo `ticket.kind`).
//
// Và `required: true` là thứ DUY NHẤT chặn được vòng lặp đốt tiền, đo được:
//   - route mới rơi vào nhánh `undefined` của `journalAttemptOutcome`
//     (supervisor-loop-outcome-journal.ts:147-158) ⇒ ticket quay lại "todo";
//   - `nextReadyTicket` (scheduler.ts:82) chọn lại NGAY ticket đó tick sau;
//   - `max_attempts_per_mission` MẶC ĐỊNH `0` = KHÔNG giới hạn
//     (config-schema.ts:188).
// ⇒ Câu hỏi không chặn ⇒ worker lặp `needs_decision` tới `DEFAULT_MAX_TICKS`,
// mỗi tick một phiên trả tiền thật. Câu hỏi chặn ⇒ worker chạy ĐÚNG MỘT LẦN
// rồi gate `interview_unanswered` giữ dispatch cho tới khi người trả lời.
// (User chốt 2026-09-16, đổi so với ý ban đầu `required: false`.)

import { createHash } from "node:crypto";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

/**
 * ID theo NỘI DUNG cho câu hỏi sinh từ `needs_decision` của MỘT ticket (V2-0,
 * spec V2 §4.0): `<ticket>-needs-decision-<sha8>`, `sha8` = 8 hex đầu sha256
 * của JSON `{summary, options}` (trim + Unicode NFC, giữ thứ tự options).
 * NFC: tiếng Việt gõ dựng sẵn và tổ hợp là cùng một câu, không được ra hai id.
 *
 * Ticket nằm TRONG id — không ghi field `ticket_id` vào interview.json: schema
 * `.strict()` của CLI ≤3.34.2 từ chối CẢ FILE khi gặp key lạ ⇒ hạ CLI giữa
 * mission sẽ mở gate và ghi đè interview.json. Lọc theo ticket dùng nguyên id
 * (needs-decision-question-ticket-match.ts; spec §4.0 cho "field HOẶC nguyên id").
 *
 * Vì sao theo nội dung chứ không cố định theo ticket như v1: `writeInterviewQuestions`
 * giữ câu trả lời của id còn trong bộ mới. Id cố định ⇒ câu hỏi MỚI của cùng
 * ticket thừa hưởng answer của câu cũ, và luật "hỏi lặp câu đã trả lời ⇒
 * contract_error" phạt oan câu mới hợp lệ. Cùng câu ⇒ cùng id (vẫn duy nhất
 * cho refine của `InterviewSchema`); câu khác ⇒ id mới, không answer nào đi theo.
 */
export function needsDecisionQuestionId(
  ticketId: string,
  summary: string,
  options: readonly string[],
): string {
  const clean = (text: string) => text.trim().normalize("NFC");
  const canonical = JSON.stringify({ summary: clean(summary), options: options.map(clean) });
  const sha8 = createHash("sha256").update(canonical, "utf8").digest("hex").slice(0, 8);
  return `${ticketId}-needs-decision-${sha8}`;
}

/** `options[]` trong result là free-form (`z.record`) — nhặt nhãn đọc được, bỏ mục không đọc được. */
function optionLabels(options: unknown): string[] {
  if (!Array.isArray(options)) return [];
  const labels: string[] = [];
  for (const option of options) {
    // KHÔNG có nhánh cho `option` là string: schema thật là
    // `options: z.array(z.record(z.string(), z.unknown()))`
    // (autopilot-result-schema.ts:116) — mỗi phần tử LUÔN là object, không phải
    // union. Bản đầu có nhánh string; đó là dead code (code-reviewer 260916 L1),
    // và một nhánh không bao giờ chạy làm người đọc sau tưởng hợp đồng rộng hơn
    // thực tế.
    if (option === null || typeof option !== "object") continue;
    const record = option as Record<string, unknown>;
    // Thứ tự ưu tiên: nhãn người đọc được trước, id kỹ thuật sau.
    for (const key of ["title", "label", "summary", "id"]) {
      const value = record[key];
      if (typeof value === "string" && value.trim().length > 0) {
        labels.push(value.trim());
        break;
      }
    }
  }
  // Trùng nhãn ⇒ `recordInterviewAnswer` không phân biệt được người chọn cái
  // nào; giữ lần xuất hiện đầu.
  return [...new Set(labels)];
}

function questionTitle(result: Record<string, unknown>, ticketId: string): string {
  const summary = result.summary;
  if (typeof summary === "string" && summary.trim().length > 0) return summary.trim();
  return `Ticket ${ticketId} cần người quyết trước khi đi tiếp.`;
}

/**
 * `undefined` khi result KHÔNG phải `needs_decision` — caller giữ nguyên đường
 * cũ. Result `needs_decision` luôn có `options[]` không rỗng (schema refine
 * `autopilot-result-schema.ts:128`), nhưng module này KHÔNG dựa vào điều đó:
 * nếu không nhặt được nhãn nào thì câu hỏi thành `text` (người tự gõ) thay vì
 * `single` không có gì để chọn — `InterviewQuestionSchema` từ chối dạng sau.
 */
export function buildNeedsDecisionQuestion(
  result: unknown,
  ticketId: string,
): InterviewQuestion | undefined {
  if (typeof result !== "object" || result === null) return undefined;
  const record = result as Record<string, unknown>;
  if (record.outcome !== "needs_decision") return undefined;

  const options = optionLabels(record.options);
  const title = questionTitle(record, ticketId);
  const base = {
    id: needsDecisionQuestionId(ticketId, title, options),
    title,
    // Xem khối lý do ở đầu file: cờ này là của CONTROLLER, không phải của worker.
    required: true as const,
    consequence: "Chưa trả lời ⇒ ticket này không dispatch tiếp (gate `interview_unanswered`).",
  };

  return options.length > 0
    ? { ...base, type: "single" as const, options }
    : { ...base, type: "text" as const };
}
